import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Typography,
  TablePagination,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Snackbar,
  TextField,
  InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AppLayout from '../../layouts/AppLayout';
import { fetchData, postData, deleteData } from '../../utils/apiClient';
import { PERMISSIONS } from '../../constants/permissions';
import { useAppConfig } from '../../provider/AppConfigProvider';
import UserFormDialog from '../../components/Users/UserFormDialog';
import ConfirmationDialog from '../../components/Dialogbox/ConfirmationDialog';

// User type definitions
interface User {
  userId: string;
  name: string;
  email: string;
  role: string;
  createdAt: number;
  updatedAt: number;
}

interface UserPagination {
  pageSize: number;
  totalItems: number;
  total: number;
  hasNext: boolean | string;
  nextToken: string;
  lastEvaluatedKey: string;
}

interface UserSearchResponse {
  users: User[];
  pagination: UserPagination;
}

const STORAGE_KEY = 'userListRowsPerPage';

interface UserListContainerProps {
  embedded?: boolean;
}

const UserListContainer: React.FC<UserListContainerProps> = ({ embedded = false }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) : 10;
  });
  const [nextToken, setNextToken] = useState('');
  const [pagination, setPagination] = useState<UserPagination | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const { initialized: flagsInitialized } = useAppConfig();

  // Search state
  const [searchText, setSearchText] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Delete confirmation dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Map to store page number to lastEvaluatedKey mapping (using ref to avoid infinite loops)
  const pageToLastEvaluatedKeyMapRef = useRef<Map<number, string>>(
    new Map([[0, '']]) // Initialize with first page having empty lastEvaluatedKey
  );

  // Common function to fetch users (list all)
  const getUsersApi = useCallback(async (): Promise<UserSearchResponse> => {
    try {
      // Get the lastEvaluatedKey for the current page from the map
      const currentPageLastEvaluatedKey =
        pageToLastEvaluatedKeyMapRef.current.get(page) || '';

      const response = await fetchData(
        `/users?page_size=${rowsPerPage}&page_number=${page}&last_evaluated_key=${currentPageLastEvaluatedKey}`,
        PERMISSIONS.GET_USERS
      );
      return response;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }, [page, rowsPerPage]);

  // Search users API function
  const searchUsersApi = useCallback(async (query: string): Promise<UserSearchResponse> => {
    try {
      const response = await postData(
        '/users/search',
        PERMISSIONS.SEARCH_USERS,
        { searchText: query }
      );
      return response;
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    // Wait for feature flags to be initialized before making decisions
    if (!flagsInitialized) {
      return;
    }

    setLoading(true);
    setError(null);
    try {

      // Use search API if searchText is provided, otherwise fetch all users
      const response = searchText.trim()
        ? await searchUsersApi(searchText.trim())
        : await getUsersApi();

      // Validate response structure
      if (!response) {
        throw new Error('Invalid response from server');
      }

      // Handle both empty array and null/undefined users
      if (response.users && Array.isArray(response.users)) {
        setUsers(response.users);

        if (response.pagination) {
          setPagination(response.pagination);
          // Handle hasNext as both boolean and string
          const hasNext =
            typeof response.pagination.hasNext === 'boolean'
              ? response.pagination.hasNext
              : String(response.pagination.hasNext).toLowerCase() === 'true';
          setHasNextPage(hasNext);
          setHasPreviousPage(page > 0);

          // Store the lastEvaluatedKey for the current page in the map
          pageToLastEvaluatedKeyMapRef.current.set(
            page,
            response.pagination.lastEvaluatedKey || ''
          );

          // Store nextToken for next request
          if (response.pagination.nextToken) {
            setNextToken(response.pagination.nextToken);
          } else {
            setNextToken('');
          }
        } else {
          // If no pagination info, assume no more pages
          setPagination(null);
          setHasNextPage(false);
          setHasPreviousPage(false);
          setNextToken('');
        }
      } else {
        // Empty or invalid items array
        setUsers([]);
        setPagination(response.pagination || null);
        setHasNextPage(false);
        setHasPreviousPage(false);
        setNextToken('');
      }
    } catch (err: any) {
      // Enhanced error handling
      let errorMessage = 'Failed to fetch users';

      if (err?.response?.status) {
        switch (err.response.status) {
          case 401:
            errorMessage = 'Unauthorized. Please check your permissions.';
            break;
          case 403:
            errorMessage =
              'Access forbidden. You do not have permission to view users.';
            break;
          case 404:
            errorMessage = 'Users endpoint not found.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage =
              err.message ||
              `Error ${err.response.status}: Failed to fetch users`;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      console.error('Error fetching users:', err);

      // Reset state on error
      setUsers([]);
      setPagination(null);
      setHasNextPage(false);
      setHasPreviousPage(false);
      setNextToken('');
    } finally {
      setLoading(false);
    }
  }, [getUsersApi, searchUsersApi, searchText, page, flagsInitialized]);

  // load data on component mount and when pagination parameters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchUsers();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [fetchUsers]);

  // Pagination handlers for MUI TablePagination
  const handleChangePage = (event: unknown, newPage: number) => {
    // Moving to next page - store nextToken for the new page
    if (newPage > page && hasNextPage && nextToken) {
      pageToLastEvaluatedKeyMapRef.current.set(newPage, nextToken);
      setPage(newPage);
    }
    // Moving to previous page - use stored lastEvaluatedKey from map
    else if (newPage < page && hasPreviousPage) {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newPageSize = parseInt(event.target.value, 10);
    setRowsPerPage(newPageSize);
    localStorage.setItem(STORAGE_KEY, newPageSize.toString());
    setPage(0);
    setNextToken('');
    pageToLastEvaluatedKeyMapRef.current = new Map([[0, '']]);
  };

  // Search handlers
  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(event.target.value);
  };

  const handleSearch = () => {
    // Reset pagination when searching
    setPage(0);
    setNextToken('');
    pageToLastEvaluatedKeyMapRef.current = new Map([[0, '']]);
    setSearchText(searchInputValue);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchInputValue('');
    setSearchText('');
    // Reset pagination
    setPage(0);
    setNextToken('');
    pageToLastEvaluatedKeyMapRef.current = new Map([[0, '']]);
  };

  const getRoleColor = (
    role: string
  ): 'error' | 'primary' | 'success' | 'default' => {
    switch (role.toUpperCase()) {
      case 'ADMIN':
        return 'error';
      case 'EDITOR':
        return 'success';
      case 'REVIEWER':
        return 'primary';
      default:
        return 'default';
    }
  };

  const formatDate = (timestamp: number | undefined): string => {
    if (!timestamp) return 'N/A';

    // convert timestamp if string
    if (typeof timestamp === 'string') {
      timestamp = parseInt(timestamp, 10);
    }

    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Snackbar handlers
  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning'
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Dialog handlers
  const handleOpenAddDialog = () => {
    setSelectedUser(null);
    setIsDialogOpen(true);
  };

  const handleOpenEditDialog = (user: User) => {
    setSelectedUser(user);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedUser(null);
  };

  const handleSaveUser = (response: any) => {
    // Show success snackbar
    const action = selectedUser ? 'updated' : 'created';
    showSnackbar(`User ${action} successfully`, 'success');
    // Refresh the user list after saving
    fetchUsers();
  };

  const handleUserError = (message: string) => {
    // Show error snackbar
    showSnackbar(message, 'error');
  };

  // Delete dialog handlers
  const handleOpenDeleteDialog = (user: User) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
      await deleteData(`/users/${userToDelete.userId}`, PERMISSIONS.DELETE_USER);
      // Show success snackbar
      showSnackbar('User deleted successfully', 'success');
      // Refresh the user list after successful deletion
      fetchUsers();
      handleCloseDeleteDialog();
    } catch (err: any) {
      console.error('Error deleting user:', err);
      showSnackbar(err.message || 'Failed to delete user', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const content = (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: embedded ? 'flex-end' : 'space-between',
          alignItems: 'center',
          mb: 3,
          ml: embedded ? 0 : 3,
          mr: embedded ? 0 : 3,
        }}
      >
        {!embedded && (
          <Typography variant="h4" gutterBottom>
            Users Dashboard
          </Typography>
        )}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                size="small"
                placeholder="Search users"
                value={searchInputValue}
                onChange={handleSearchInputChange}
                onKeyPress={handleSearchKeyPress}
                sx={{ minWidth: 250 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchInputValue && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        edge="end"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                onClick={handleSearch}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                Search
              </Button>
            </Box>
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenAddDialog}
                sx={{ textTransform: 'none' }}
              >
                Add New User
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Data Table */}
        <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created At</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Updated At</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No users found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user, index) => (
                  <TableRow key={user.userId || index} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          color: 'primary.main',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                        onClick={() => handleOpenEditDialog(user)}
                      >
                        {user.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>{user.email || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role || 'N/A'}
                        color={getRoleColor(user.role)}
                        size="small"
                        sx={{ fontSize: '0.75rem', fontWeight: 'bold', fontFamily: 'consolas' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {formatDate(user.createdAt)}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {formatDate(user.updatedAt)}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Delete user">
                        <IconButton
                          color="error"
                          onClick={() => handleOpenDeleteDialog(user)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={pagination?.totalItems || -1}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) => {
            if (pagination?.total) {
              return `${from}-${to} of ${pagination?.total ? pagination.total : count}`;
            }
            return `${from}-${to} of more than ${to}`;
          }}
          backIconButtonProps={{
            disabled: !hasPreviousPage || loading,
          }}
          nextIconButtonProps={{
            disabled: !hasNextPage || loading,
          }}
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        />

        {/* User Form Dialog */}
        <UserFormDialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          onSave={handleSaveUser}
          onError={handleUserError}
          user={selectedUser}
        />

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          open={isDeleteDialogOpen}
          title="Delete User"
          message={`Are you sure you want to delete user "${userToDelete?.name || userToDelete?.email}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={handleCloseDeleteDialog}
          confirmButtonColor="error"
          disabled={isDeleting}
        />

      {/* Snackbar for API notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );

  if (embedded) {
    return content;
  }

  return <AppLayout>{content}</AppLayout>;
};

export default UserListContainer;
